import { Component } from 'preact';
import { Form, FormFieldOption } from '../../common/data/forms.js';
import { ContentExtras } from '../../common/data/content.js';
import { ContentBlock, ContentContext } from './content.js';
import { classNames, getEventValue, submitQueue } from '../lib/util.js';
import { isNone } from '../../common/util.js';
import { Input } from './input.js';
import { Checkbox } from './checkbox.js';
import { TargetedEvent, useContext } from 'preact/compat';
import { showAlert } from './popup.js';
import { submitForm } from '../lib/api.js';
import { SiteContext } from '../context.js';

export interface FormBlockProps {
	form: Form;
	mode: 'page' | 'block'
}
interface FormPageState {
	answers: Record<string, any>;
	attempted: boolean;
	success: boolean;
}

export class FormBlock extends Component<FormBlockProps, FormPageState> {

	constructor(props: FormBlockProps) {
		super(props);
		this.state = {
			answers: {},
			attempted: false,
			success: false,
		};
	}

	render() {
	
		const { form, mode } = this.props;
		const { answers, attempted, success } = this.state;
		
		const optId = (opt: (FormFieldOption | string)) => typeof opt == 'string' ? opt : opt.id;
		const isPage = mode == 'page';
		
		return <form onSubmit={this.submit.bind(this)} class={classNames(
			'dyn-form',
			attempted && '--attempted',
			`--${mode}`,
		)} action="#" method="post">
			
			
			{isPage && <h1>{form.title}</h1>}
			
			{!success && <>
				
				{isPage && form.description && <div class="dyn-form__description">
					<ContentBlock item={form.description} />
				</div>}
				
				<div class="dyn-form__fields">
					
					{form.fields.map(f => {
						const setValue = (v: any) => this.setState({ answers: { ...answers, [f.id]: v || null } });
						const withLabel = f.type != 'checkbox';
						return <div key={f.id} class={classNames('dyn-form__field', `--${f.type}`)} style={f.size ? { '--size': f.size } : undefined}>
							
							{f.required && <div class={classNames('dyn-form__field-required', attempted && isNone(answers[f.id]) && '--error')} title="Required">*</div>}
							
							{withLabel && <label class="dyn-form__field-label">
								{f.label}
							</label>}
							
							{f.type == 'checkbox' && <Checkbox label={f.description} checked={!!answers[f.id]} onChange={setValue} />}
							
							{withLabel && !!f.description && <div class="dyn-form__field-description">{f.description}</div>}
							
							{f.type == 'text' && <Input value={answers[f.id] ?? ''} handler={s => setValue(s.trim())} attributes={{ name: f.id }} />}
							{f.type == 'textarea' && <Input textarea={true} value={answers[f.id] ?? ''} handler={setValue} attributes={{ name: f.id }} />}
							
							{f.type == 'select' && <select value={answers[f.id] ?? ''} onChange={e => setValue(getEventValue(e))}>
								<option value="" />
								{f.options?.map(opt => (
									typeof opt == 'string' ? <option key={opt} value={opt}>{opt}</option>
									: <option key={opt.id} value={opt.id}>{opt.label}</option>
								))}
							</select>}
							{f.type == 'radio' && f.options?.map(opt => <Checkbox
								key={optId(opt)}
								radio={true}
								label={typeof opt == 'string' ? opt : opt.label}
								checked={answers[f.id] == optId(opt)}
								onChange={on => { if (on) setValue(optId(opt)); }}
							/>)}
							{f.type == 'multichoice' && f.options?.map(opt => <Checkbox
								key={optId(opt)}
								radio={false}
								label={typeof opt == 'string' ? opt : opt.label}
								checked={answers[f.id]?.includes(optId(opt))}
								onChange={on => {
									const id = optId(opt);
									setValue([
										...answers[f.id]?.filter((v: string) => v != id) ?? [],
										...on ? [id] : [],
									]);
								}}
							/>)}
							
						</div>;
					})}
				
				</div>
				
				<div class="dyn-form__submit">
					<button>{form.submit_label ?? 'Submit'}</button>
				</div>
				
			</>}
			
			{success ? (form.success_message ? <ContentBlock item={form.success_message} /> : 'Submitted successfully.') : null}
			
			
			
		</form>;
		
	}
	
	@submitQueue
	async submit(e: TargetedEvent) {
		
		e.preventDefault();
		e.stopPropagation();
		
		const { form } = this.props;
		const { answers } = this.state;
		
		this.setState({ attempted: true });
		
		const missing = form.fields.filter(f => f.required && isNone(answers[f.id]));
		if (missing.length) return await showAlert(<div style={{ textAlign: 'left' }}>
			<div>Please fill in the required fields:</div>
			<ul>
				{missing.map(f => <li>{f.label}</li>)}
			</ul>
		</div>);
		
		await submitForm({
			form: form.id,
			version: form.version,
			answers,
		});
		
		this.setState({ success: true });
		
	}
	
}

export interface FormPageProps extends Omit<FormBlockProps, 'mode'> {
	extras: ContentExtras;
}

export function FormPage({ extras, ...props }: FormPageProps) {
	const domain = useContext(SiteContext).domain.value;
	return <ContentContext.Provider value={{
		target: ['web'],
		domain,
		website: `https://${domain.host}`,
		extras,
		link: s => s,
		image: s => s,
	}}>
		<FormBlock mode="page" {...props} />
	</ContentContext.Provider>;
}
